import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import Typography from "../../components/Typography/Typography";
import SourcesSelection, { SourceItem } from "./SourcesSelection";
import Chat, { ChatMessage } from "./Chat";
import GeneratedContent, { GeneratedContentData } from "./GeneratedContent";
import {
  chatGeneratedContent,
  getArticle,
  getChatMessages,
  getSourcesOptions,
  updateSelectedSources,
} from "../../services/common.service";
import { getToast } from "../../common/utils";
import DashboardStyles from "./Dashboard.module.scss";
import { useAppDispatch, useAppSelector } from "../../hooks/store";
import { createArticleSessionThunk } from "../../store/thunks/common.thunk";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../../common/constants";

interface ApiOption {
  option: string;
  selected: boolean;
}

interface ApiModel {
  model: string;
  selected: boolean;
}

interface ApiResponse {
  status: boolean;
  options: ApiOption[];
  llm_models: ApiModel[];
}

// Main Dashboard Component
const Dashboard: React.FC = () => {
  const { session_id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // const session_id = useAppSelector(
  //   (store) => store?.rootReducer?.commonReducer?.session_id
  // );

  const [sources, setSources] = useState<SourceItem[]>([
    { id: "0", name: "Select All", selected: false, isSelectAll: true },
  ]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSourcesCollapsed, setIsSourcesCollapsed] = useState(false);
  const [generatedContent, setGeneratedContent] =
    useState<GeneratedContentData | null>(null);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [hasStartedChat, setHasStartedChat] = useState(false);
  const [selectedModel, setSelectedModel] = useState<string>("database");
  const [apiData, setApiData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isGenetingResponse, setIsGeneratingResponse] = useState(false);
  const [relatedQuestions, setRelatedQuestions] = useState<string[]>([]);

  useEffect(() => {
    const ensureSession = async () => {
      if (!session_id) {
        await dispatch(createArticleSessionThunk());
      }
    };
    ensureSession();
  }, [session_id, dispatch]);

  useEffect(() => {
    if (!session_id) {
      navigate(ROUTES.dashboard);
      return;
    }

    const fetchAllData = async () => {
      setLoading(true);
      try {
        const [sourcesRes, chatRes, articleRes] = await Promise.all([
          getSourcesOptions(session_id),
          getChatMessages(session_id),
          getArticle(session_id),
        ]);
        setApiData(sourcesRes);
        updateSourcesBasedOnModel("database", sourcesRes);
        const resppp = getChatMessages(session_id);
        console.log(resppp);

        setMessages(
          Array.isArray(chatRes.messages)
            ? chatRes.messages.map((msg: any) => ({
                id: msg.id,
                text: msg.message,
                isUser: msg.is_user,
                timestamp: new Date(msg.created_at),
              }))
            : []
        );

        setGeneratedContent(
          articleRes && articleRes.article
            ? {
                title: articleRes.title,
                content: articleRes.article,
                sources: articleRes.sources || [],
                article_with_citations: articleRes.article_with_citations || "",
                citations: articleRes.citations || {},
                citations_dict: articleRes.citations_dict || {},
                sources_status: articleRes.sources || {},
              }
            : null
        );
      } catch (error) {
        getToast("Error", error || "Error in fetching data!");
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [session_id]);

  const updateSourcesBasedOnModel = (
    model: string,
    data: ApiResponse = apiData!
  ) => {
    if (!data) return;

    let newSources: SourceItem[] = [
      { id: "0", name: "Select All", selected: false, isSelectAll: true },
    ];

    if (model === "database") {
      // Use options for database
      const optionSources = data.options.map((option, index) => ({
        id: (index + 1).toString(),
        name: option.option,
        selected: option.selected,
        isSelectAll: false,
      }));
      newSources = [...newSources, ...optionSources];
    } else if (model === "gemini") {
      // Use llm_models for gemini
      const modelSources = data.llm_models.map((model, index) => ({
        id: (index + 1).toString(),
        name: model.model,
        selected: model.selected,
        isSelectAll: false,
      }));
      newSources = [...newSources, ...modelSources];
    }

    // Update "Select All" based on individual selections
    const nonSelectAllSources = newSources.filter((s) => !s.isSelectAll);
    const allSelected =
      nonSelectAllSources.length > 0 &&
      nonSelectAllSources.every((s) => s.selected);
    newSources[0].selected = allSelected;

    setSources(newSources);
  };

  const handleModelChange = (model: string) => {
    setSelectedModel(model);
    updateSourcesBasedOnModel(model);
  };

  const handleSourceToggle = async (id: string) => {
    if (!session_id) {
      getToast("Error", "Session ID is missing.");
      return;
    }
    let updatedSources: SourceItem[] = [];

    if (id === "0") {
      const selectAllSource = sources.find((s) => s.id === "0");
      const newSelectedState = !selectAllSource?.selected;
      updatedSources = sources.map((source) => ({
        ...source,
        selected: newSelectedState,
      }));
      setSources(updatedSources);
    } else {
      updatedSources = sources.map((source) =>
        source.id === id ? { ...source, selected: !source.selected } : source
      );
      const nonSelectAllSources = updatedSources.filter((s) => !s.isSelectAll);
      const allSelected =
        nonSelectAllSources.length > 0 &&
        nonSelectAllSources.every((s) => s.selected);
      updatedSources = updatedSources.map((source) =>
        source.isSelectAll ? { ...source, selected: allSelected } : source
      );
      setSources(updatedSources);
    }

    if (selectedModel === "database") {
      const selectedOptions = updatedSources
        .filter((s) => s.selected && !s.isSelectAll)
        .map((s) => s.name);

      try {
        await updateSelectedSources(session_id, { options: selectedOptions });
      } catch (error) {
        // Handle error if needed
      }
    }
  };

  const handleSendMessage = async (message: string) => {
    if (!session_id) {
      getToast("Error", "Session ID is missing.");
      return;
    }
    // Add user's message to chat
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      text: message,
      isUser: true,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
    // setIsGeneratingContent(true);
    setIsGeneratingResponse(true);

    try {
      const response = await chatGeneratedContent(session_id, {
        query: message,
      });

      // Add AI chat response to chat
      if (response?.chat) {
        const aiResponse: ChatMessage = {
          id: (Date.now() + 1).toString(),
          text: response.chat,
          isUser: false,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, aiResponse]);
      }

      if (
        response?.related_questions &&
        Array.isArray(response.related_questions)
      ) {
        setRelatedQuestions(response.related_questions);
      }

      // Show generated content only if article is not empty
      if (response?.article && response.article.trim() !== "") {
        setGeneratedContent({
          title: response.title,
          content: response.article,
          sources: sources
            .filter((s) => s.selected && !s.isSelectAll)
            .map((s) => s.name),
          article_with_citations: response.article_with_citations,
          citations: response.citations,
          citations_dict: response.citations_dict,
          sources_status: response.sources,
        });
      }
      // If article is empty, do NOT update generated content (leave as is)
    } catch (error) {
      // Handle error (optional: show toast)
      getToast("Error", "Failed to generate content.");
    } finally {
      setIsGeneratingContent(false);
      setIsGeneratingResponse(false);
      if (!hasStartedChat) {
        setHasStartedChat(true);
        setIsSourcesCollapsed(true);
      }
    }
  };

  const handleToggleSourcesCollapse = () => {
    setIsSourcesCollapsed(!isSourcesCollapsed);
  };

  // Handler for when content is updated (including title changes)
  const handleContentUpdate = (updatedContent: GeneratedContentData) => {
    setGeneratedContent(updatedContent);
  };

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography>Loading sources...</Typography>
      </Box>
    );
  }

  return (
    <div className={DashboardStyles.dashWrapper}>
      <Box sx={{ p: 3, height: "100vh", width: "100vw" }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            height: "80vh",
            overflow: "hidden",
          }}
        >
          {/* Sources Selection */}
          <Box
            sx={{
              flex: isSourcesCollapsed ? "0 0 auto" : "1",
              borderRadius: "16px",
            }}
          >
            <SourcesSelection
              sources={sources}
              onSourceToggle={handleSourceToggle}
              isCollapsed={isSourcesCollapsed}
              onToggleCollapse={handleToggleSourcesCollapse}
              selectedModel={selectedModel}
              onModelChange={handleModelChange}
            />
          </Box>

          {/* Chat */}
          <Box sx={{ flex: "2" }}>
            <Chat
              messages={messages}
              onSendMessage={handleSendMessage}
              isExpanded={hasStartedChat}
              isLoading={isGenetingResponse}
              suggestions={relatedQuestions}
            />
          </Box>

          {/* Generated Content (only shown after chat starts) */}
          {(generatedContent || isGeneratingContent) && (
            <Box sx={{ flex: "2" }}>
              <GeneratedContent
                content={generatedContent}
                isLoading={isGeneratingContent}
                onContentUpdate={handleContentUpdate}
              />
            </Box>
          )}
        </Box>
        <Typography
          fontWeight={400}
          fontSize={10}
          className={DashboardStyles.disclaimerText}
        >
          Content generated by this tool is created with the help of artificial
          intelligence. Results may contain errors, inaccuracies, or outdated
          information. Please review and verify all outputs before relying on
          them or sharing with others.
        </Typography>
      </Box>
    </div>
  );
};

export default Dashboard;
